import { ArrowUpRight } from '@phosphor-icons/react';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ButtonThemeColor } from '@tmh-website/ui/Button';
import type { DropdownThemeColor } from '@tmh-website/ui/Dropdown';
import type { LocalePrefix } from '@tmh-website/shared/const';
import ButtonIcon from '@tmh-website/ui/ButtonIcon';
import CMSLink from '@tmh-website/components/shared/CMSLink';
import DropdownHover from '@tmh-website/ui/DropdownHover';
import Search from '@tmh-website/components/icons/phosphor-extension/icons/Search';
import User from '@tmh-website/components/icons/phosphor-extension/icons/User';
import { Audience, Project, hasShop } from '@tmh-website/shared/const';
import { HeartStraightWithIndicator, ShoppingCartSimpleWithIndicator } from '@tmh-website/components/icons/interactive';
import { isProjectWithSearch } from '@tmh-website/services/search.client.service';
import { isValidLinkField } from '@tmh-website/shared/cms';
import { useSearchAtom } from '@tmh-website/shared/hooks';
import {
  CustomerCartContext,
  CustomerContext,
  GlobalContext,
  ProjectContext,
  useHomepageLink,
  useIsMobile,
} from '@tmh-website/shared';

import AccountDropdown from '../AccountDropdown';
import MiniCart from '../MiniCart';

import * as Styled from './HeaderButtons.styles';
import { LinkCustomerPortal, LinkPartnershop } from './partials';

interface HeaderButtonsProps {
  audience: Audience | null;
  audienceIsLoaded: boolean;
  buttonsThemeColor: ButtonThemeColor;
  dropdownsThemeColor: DropdownThemeColor;
  inMenu?: boolean;
  isStandaloneMode: boolean;
  locale: LocalePrefix;
  outlineButtonThemeColor: ButtonThemeColor;
}

const HeaderButtons = ({
  audience,
  audienceIsLoaded,
  buttonsThemeColor,
  dropdownsThemeColor,
  inMenu,
  isStandaloneMode,
  locale,
  outlineButtonThemeColor,
}: HeaderButtonsProps) => {
  const { t } = useTranslation();
  const { wishlist } = useContext(CustomerContext);
  const { cart } = useContext(CustomerCartContext);
  const isMobile = useIsMobile();
  const { project } = useContext(ProjectContext);
  const [activeDropdownHover, setActiveDropdownHover] = useState<'cart' | 'account'>();
  const [shouldRenderShopButtons, setShouldRenderShopButtons] = useState(false);
  const homepageLink = useHomepageLink({ locale });
  const { open: openSearch } = useSearchAtom();
  const {
    headerLink,
    isHeaderAccountButtonVisible = true,
    isHeaderCartButtonVisible = true,
    isHeaderSearchButtonVisible = true,
    isHeaderFavoritesButtonVisible = true,
  } = useContext(GlobalContext);

  const isHome = homepageLink === (typeof window !== 'undefined' ? window.location.pathname : '');
  const isOurCompanyHome = audience !== Audience.OurCompany || (isHome && audience === Audience.OurCompany);

  useEffect(() => {
    setShouldRenderShopButtons(
      hasShop(locale, project) && audienceIsLoaded && isOurCompanyHome && audience !== Audience.BusinessPartners
    );
  }, [audienceIsLoaded, audience, isHome]);

  return (
    <Styled.Container>
      {/* "Search" button */}
      {isProjectWithSearch(project) && isHeaderSearchButtonVisible && (
        <ButtonIcon
          themeColor={buttonsThemeColor}
          isTransparent
          onClick={openSearch}
          aria-label={t('global:menu.openSearch') as string}>
          <Search size={40} isSmall />
        </ButtonIcon>
      )}
      {/* END | "Search" button */}

      {/* Optional (custom) link from the CMS */}
      {isValidLinkField(headerLink) && (
        <CMSLink link={headerLink}>
          <Styled.AdjustedButtonOutlined
            $hideOnMobile={!inMenu}
            iconRight={<ArrowUpRight weight='bold' size={20} />}
            isSmall={true}
            themeColor={outlineButtonThemeColor}>
            {headerLink.label}
          </Styled.AdjustedButtonOutlined>
        </CMSLink>
      )}
      {/* END | Optional (custom) link from the CMS */}

      {/* "Partnershop" link */}
      {project === Project.Website && audience === Audience.BusinessPartners && (
        <LinkPartnershop {...{ inMenu, outlineButtonThemeColor }} />
      )}
      {/* END | "Partnershop" link */}

      {/* "Account" button/dropdown */}
      {shouldRenderShopButtons && isHeaderAccountButtonVisible && (
        <DropdownHover
          defaultPlacement='bottom-end'
          themeColor={dropdownsThemeColor}
          onOpenChange={open => {
            if (open === true) {
              setActiveDropdownHover('account');
            }
          }}
          forceClose={activeDropdownHover !== 'account'}
          anchorEl={
            <Styled.AccountButton
              $hideOnMobile={!inMenu}
              href={isMobile === true ? undefined : `/${locale}/customer/account/login`}
              maskLink
              themeColor={buttonsThemeColor}
              isTransparent
              aria-label={t('global:menu.myAccount') as string}>
              <User size={40} isSmall />
            </Styled.AccountButton>
          }>
          <AccountDropdown dropdownThemeColor={dropdownsThemeColor} locale={locale} />
        </DropdownHover>
      )}
      {/* END | "Account" button/dropdown */}

      {/* "Favorites" button */}
      {shouldRenderShopButtons && isHeaderFavoritesButtonVisible && (
        <Styled.WishlistButton
          $hideOnMobile={!inMenu}
          href={`/${locale}/wishlist`}
          maskLink
          themeColor={buttonsThemeColor}
          isTransparent
          aria-label={t('global:menu.showWishlist') as string}>
          <HeartStraightWithIndicator size={40} indicatorActive={(wishlist?.items?.length ?? 0) > 0} />
        </Styled.WishlistButton>
      )}
      {/* END | "Favorites" button */}

      {/* "Cart" button/dropdown */}
      {shouldRenderShopButtons && isHeaderCartButtonVisible && (
        <DropdownHover
          defaultPlacement='bottom-end'
          themeColor={dropdownsThemeColor}
          onOpenChange={open => {
            if (open === true) {
              setActiveDropdownHover('cart');
            }
          }}
          forceClose={activeDropdownHover !== 'cart'}
          anchorEl={
            <Styled.CartButton
              $hideOnMobile={!inMenu}
              href={isMobile === true ? undefined : `/${locale}/checkout/cart`}
              maskLink
              themeColor={buttonsThemeColor}
              isTransparent
              aria-label={t('global:menu.showCart') as string}>
              <ShoppingCartSimpleWithIndicator size={40} amount={cart?.totalQuantity ?? 0} />
            </Styled.CartButton>
          }>
          <MiniCart isStandaloneMode={isStandaloneMode} locale={locale} />
        </DropdownHover>
      )}
      {/* END | "Cart" button/dropdown */}

      {/* "Customer portal" link */}
      {project === Project.ElectricityTariff && <LinkCustomerPortal themeColor={buttonsThemeColor} />}
      {/* END | "Customer portal" link */}
    </Styled.Container>
  );
};

export default HeaderButtons;
